import {
  createContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useLocalStorage, useDebouncedState } from "@mantine/hooks";
import OrganisationService from "../services/OrganisationService";

export const OrganisationsContext = createContext(null);

export const OrganisationsProvider = ({ children }) => {
  const [organisations, setOrganisations] = useState([]);
  const [organisationsCount, setOrganisationsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);

  const [pagination, setPagination] = useLocalStorage({
    key: "organisations-pagination",
    defaultValue: { pageIndex: 0, pageSize: 25 },
    getInitialValueInEffect: false,
  });
  const [sorting, setSorting] = useLocalStorage({
    key: "organisations-sorting",
    defaultValue: [],
    getInitialValueInEffect: false,
  });
  const [columnPinning, setColumnPinning] = useLocalStorage({
    key: "organisations-column-pinning",
    defaultValue: { left: [], right: ["actions"] },
    getInitialValueInEffect: false,
  });
  const [columnVisibility, setColumnVisibility] = useLocalStorage({
    key: "organisations-column-visibility",
    defaultValue: {},
    getInitialValueInEffect: false,
  });
  const [columnOrder, setColumnOrder] = useLocalStorage({
    key: "organisations-column-order",
    defaultValue: [],
    getInitialValueInEffect: false,
  });
  const [orgTypeFilter, setOrgTypeFilterState] = useLocalStorage({
    key: "organisations-org-type-filter",
    defaultValue: "all",
    getInitialValueInEffect: false,
  });
  const [countryFilter, setCountryFilterState] = useLocalStorage({
    key: "organisations-country-filter",
    defaultValue: "all",
    getInitialValueInEffect: false,
  });
  const [counts, setCounts] = useState(null);

  const [search, setSearch] = useDebouncedState("", 500);

  const setOrgTypeFilter = useCallback(
    (next) => {
      setOrgTypeFilterState(next);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setOrgTypeFilterState, setPagination]
  );

  const setCountryFilter = useCallback(
    (next) => {
      setCountryFilterState(next);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setCountryFilterState, setPagination]
  );

  const handleSetSearch = useCallback(
    (value) => {
      setSearch(value);
      if (pagination.pageIndex !== 0) {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
    },
    [setSearch, pagination.pageIndex, setPagination]
  );

  const fetchOrganisations = useCallback(
    async (params = {}) => {
      setLoading((prev) => (organisations?.length === 0 ? true : prev));
      if (organisations?.length > 0) {
        setIsRefetching(true);
      }

      try {
        const ordering = sorting.length
          ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
          : "";
        const orgTypeParam =
          orgTypeFilter === "all" ? undefined : orgTypeFilter;
        const countryParam =
          countryFilter === "all" ? undefined : countryFilter;

        const response = await OrganisationService.getOrganisations({
          page_size: pagination.pageSize,
          page: pagination.pageIndex + 1,
          search,
          ordering,
          org_type: orgTypeParam,
          country: countryParam,
          ...params,
        });

        setOrganisations(response.organisations);
        setOrganisationsCount(response.count);
      } catch (error) {
        console.error("Error fetching organisations:", error);
      } finally {
        setLoading(false);
        setIsRefetching(false);
      }
    },
    [
      pagination.pageSize,
      pagination.pageIndex,
      search,
      sorting,
      organisations?.length,
      orgTypeFilter,
      countryFilter,
    ]
  );

  const fetchCounts = useCallback(async () => {
    const next = await OrganisationService.getOrganisationCounts({ search });
    setCounts(next);
  }, [search]);

  const memoizedSorting = useMemo(() => sorting, [JSON.stringify(sorting)]);

  useEffect(() => {
    fetchOrganisations();
  }, [
    pagination.pageSize,
    pagination.pageIndex,
    memoizedSorting,
    search,
    fetchOrganisations,
  ]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const createOrganisation = useCallback(
    async (newOrgPayload) => {
      try {
        await OrganisationService.createOrganisation(newOrgPayload);
        fetchOrganisations();
      } catch (error) {
        console.error("Error creating organisation:", error);
      }
    },
    [fetchOrganisations]
  );

  return (
    <OrganisationsContext.Provider
      value={{
        organisations,
        organisationsCount,
        loading,
        isRefetching,
        pagination,
        setPagination,
        sorting,
        setSorting,
        search,
        setSearch: handleSetSearch,
        columnPinning,
        setColumnPinning,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        orgTypeFilter,
        setOrgTypeFilter,
        countryFilter,
        setCountryFilter,
        counts,
        fetchOrganisations,
        createOrganisation,
      }}
    >
      {children}
    </OrganisationsContext.Provider>
  );
};
